import { instrumentRunQueryKeys } from '@/config/queryKeys';
import InstrumentRunHeader from '@/pages/Instrument_Runs/components/InstrumentRunHeader/InstrumentRunHeader';
import RunSamplesList from '@/pages/Instrument_Runs/components/RunSamplesList';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { useAppStore } from '@/stores/app';
import { InstrumentRunPublic, Sample } from '@/types';
import { FullLoading } from '@components/index';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { type UseMutationResult, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, Link, createFileRoute, redirect } from '@tanstack/react-router';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { MutationContext, UpdateVariables, useUpdateDescription } from '../../pages/Instrument_Runs';
import irStyles from '../../pages/Instrument_Runs/styles.module.css';
import styles from '../../pages/Studies/styles.module.css';
import pageStyles from '../../styles/pages/index.module.css';

const instrumentRunSearchSchema: z.ZodType<Record<string, never>> = z.object({});

export const Route = createFileRoute('/instrument_runs/$instrumentRunId')({
  beforeLoad: ({ context }) => {
    // Redirect to login if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }
  },
  loader: async ({ context, params }) => {
    const selectedOrg = useAppStore.getState()?.selectedOrganization;

    if (!selectedOrg?.id) {
      throw redirect({
        to: '/organizations',
      });
    }

    // Invalidate and fetch fresh data from the backend to ensure we have the latest
    // This prevents stale data from being used when redirecting to edit mode
    await context.queryClient.invalidateQueries({
      queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId),
    });

    // Fetch the instrument run to check its status
    const instrumentRun = await context.queryClient.ensureQueryData<InstrumentRunPublic>({
      queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId),
      queryFn: () => fetchInstrumentRun(selectedOrg.id, params.instrumentRunId),
    });

    // If the run is in DRAFT status, redirect to the edit page in draft mode
    // The loader on /instrument_runs/new will fetch the data from backend
    if (instrumentRun?.status === 'DRAFT') {
      throw redirect({
        to: '/instrument_runs/new',
        search: {
          mode: 'draft' as const,
          id: instrumentRun.id,
        },
      });
    }

    return { instrumentRun };
  },
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('runs');

    return <FullLoading label={`${t('run.fetching')}...`} />;
  },
  validateSearch: instrumentRunSearchSchema,
});

function RouteComponent() {
  const { t } = useTranslation('runs');
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));
  const { instrumentRunId } = Route.useParams();

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error('No organization selected')} />;
  }

  const { data } = useSuspenseQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, instrumentRunId),
    queryFn: () => fetchInstrumentRun(selectedOrg.id, instrumentRunId),
  });
  const gridRef = useRef<AgGridReact<Sample> | null>(null);

  const emptyTxt = t('empty', { ns: 'general' });

  // Mutation for updating the description
  const updateDescriptionMutation: UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext> =
    useUpdateDescription(selectedOrg.id, instrumentRunId, data);

  return (
    <Stack fullHeight fullWidth orientation="vertical">
      <Link
        className={clsx(
          pageStyles.titleRow,
          pageStyles.organizationTitle,
          getTextClassName('lg'),
          styles.studyTitleBar,
        )}
        preload="render"
        to="/instrument_runs"
      >
        {t('run.back')}
      </Link>

      <Stack alignItems="start" className={irStyles.contentContainer} fullHeight gap={3} orientation="vertical">
        {data ? (
          <>
            <InstrumentRunHeader run={data} updateDescriptionMutation={updateDescriptionMutation} />

            {/* Render samples array in a table if present */}
            {data?.samples?.length > 0 && (
              <RunSamplesList id={data.id} samples={data.samples} numSamples={data.num_samples} gridRef={gridRef} />
            )}
          </>
        ) : (
          <Text>{emptyTxt}</Text>
        )}
      </Stack>
    </Stack>
  );
}
